import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { Calendar, Search, Trash2, Undo2, Filter, Clock, User, Gift } from "lucide-react"
import { format } from "date-fns"
import { de } from "date-fns/locale"
import { DashboardLayout } from "@/components/layout/DashboardLayout"

interface StampWithCustomer {
  id: string
  created_at: string
  points_awarded: number
  stamp_type: 'visit' | 'referral'
  customer: {
    id: string
    full_name: string
    email: string
  }
}

export default function StampManagement() {
  const { user, loading: authLoading } = useAuth()
  const { toast } = useToast()
  const [stamps, setStamps] = useState<StampWithCustomer[]>([])
  const [filteredStamps, setFilteredStamps] = useState<StampWithCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [selectedStamps, setSelectedStamps] = useState<string[]>([])
  const [dateFilter, setDateFilter] = useState<string>("all")

  useEffect(() => {
    if (!authLoading) {
      fetchStamps()
    }
  }, [authLoading])

  useEffect(() => {
    filterStamps()
  }, [stamps, searchTerm, typeFilter, dateFilter])

  const fetchStamps = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Get user's tenant ID first
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .maybeSingle();

      if (tenantError) throw tenantError;
      if (!tenantData) {
        console.log('No tenant found for admin user:', user.id);
        setStamps([]);
        setLoading(false);
        return;
      }

      const { data: stampsData, error } = await supabase
        .from('stamps')
        .select(`
          id,
          created_at,
          points_awarded,
          stamp_type,
          customers!customer_id(id, full_name, email)
        `)
        .eq('tenant_id', tenantData.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase stamps query error:', error);
        throw error;
      }

      console.log('Successfully fetched stamps:', stampsData?.length || 0);
      setStamps((stampsData as any[])?.map(stamp => ({
        id: stamp.id,
        created_at: stamp.created_at,
        points_awarded: stamp.points_awarded,
        stamp_type: stamp.stamp_type,
        customer: stamp.customers
      })) || []);
    } catch (error) {
      console.error('Error fetching stamps:', error);
      toast({
        title: "Fehler",
        description: "Stempel konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const filterStamps = () => {
    let filtered = stamps

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(stamp => 
        stamp.customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stamp.customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(stamp => stamp.stamp_type === typeFilter)
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date()
      const filterDate = new Date()
      
      switch (dateFilter) {
        case "today":
          filterDate.setHours(0, 0, 0, 0)
          filtered = filtered.filter(stamp => new Date(stamp.created_at) >= filterDate)
          break
        case "week":
          filterDate.setDate(now.getDate() - 7)
          filtered = filtered.filter(stamp => new Date(stamp.created_at) >= filterDate)
          break
        case "month":
          filterDate.setMonth(now.getMonth() - 1)
          filtered = filtered.filter(stamp => new Date(stamp.created_at) >= filterDate)
          break
      }
    }

    setFilteredStamps(filtered)
  }

  const handleUndoStamp = async (stampId: string) => {
    try {
      // Get stamp details first
      const stamp = stamps.find(s => s.id === stampId)
      if (!stamp) return

      // Delete the stamp
      const { error: deleteError } = await supabase
        .from('stamps')
        .delete()
        .eq('id', stampId)

      if (deleteError) throw deleteError

      // Update customer's stamp counts
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select('visit_stamps, referral_stamps, points')
        .eq('id', stamp.customer.id)
        .single()

      if (fetchError) throw fetchError

      const updates: any = {
        points: Math.max(0, customer.points - stamp.points_awarded)
      }

      if (stamp.stamp_type === 'visit') {
        updates.visit_stamps = Math.max(0, customer.visit_stamps - 1)
      } else {
        updates.referral_stamps = Math.max(0, customer.referral_stamps - 1)
      }

      const { error: updateError } = await supabase
        .from('customers')
        .update(updates)
        .eq('id', stamp.customer.id)

      if (updateError) throw updateError

      await fetchStamps()
      toast({
        title: "Erfolg",
        description: "Stempel wurde rückgängig gemacht.",
      })
    } catch (error) {
      console.error('Error undoing stamp:', error)
      toast({
        title: "Fehler",
        description: "Stempel konnte nicht rückgängig gemacht werden.",
        variant: "destructive",
      })
    }
  }

  const handleBulkDelete = async () => {
    try {
      // Delete selected stamps
      const { error: deleteError } = await supabase
        .from('stamps')
        .delete()
        .in('id', selectedStamps)

      if (deleteError) throw deleteError

      // Update customer counts for each affected customer
      const affectedStamps = stamps.filter(s => selectedStamps.includes(s.id))
      const customerUpdates = new Map()

      for (const stamp of affectedStamps) {
        const customerId = stamp.customer.id
        if (!customerUpdates.has(customerId)) {
          const { data: customer } = await supabase
            .from('customers')
            .select('visit_stamps, referral_stamps, points')
            .eq('id', customerId)
            .single()

          if (customer) {
            customerUpdates.set(customerId, { ...customer })
          }
        }

        const customerData = customerUpdates.get(customerId)
        if (customerData) {
          customerData.points = Math.max(0, customerData.points - stamp.points_awarded)
          if (stamp.stamp_type === 'visit') {
            customerData.visit_stamps = Math.max(0, customerData.visit_stamps - 1)
          } else {
            customerData.referral_stamps = Math.max(0, customerData.referral_stamps - 1)
          }
        }
      }

      // Apply updates
      for (const [customerId, updates] of customerUpdates) {
        await supabase
          .from('customers')
          .update({
            visit_stamps: updates.visit_stamps,
            referral_stamps: updates.referral_stamps,
            points: updates.points
          })
          .eq('id', customerId)
      }

      await fetchStamps()
      setSelectedStamps([])
      toast({
        title: "Erfolg",
        description: `${selectedStamps.length} Stempel wurden gelöscht.`,
      })
    } catch (error) {
      console.error('Error bulk deleting stamps:', error)
      toast({
        title: "Fehler",
        description: "Stempel konnten nicht gelöscht werden.",
        variant: "destructive",
      })
    }
  }

  const toggleStampSelection = (stampId: string) => {
    setSelectedStamps(prev => 
      prev.includes(stampId)
        ? prev.filter(id => id !== stampId)
        : [...prev, stampId]
    )
  }

  const toggleSelectAll = () => {
    setSelectedStamps(
      selectedStamps.length === filteredStamps.length 
        ? [] 
        : filteredStamps.map(s => s.id)
    )
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Stempel-Verwaltung</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Lade Stempel...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Stempel-Verwaltung</h1>
          <div className="flex gap-2">
          {selectedStamps.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {selectedStamps.length} löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Stempel löschen</AlertDialogTitle>
                  <AlertDialogDescription>
                    Möchten Sie {selectedStamps.length} Stempel wirklich löschen? 
                    Diese Aktion kann nicht rückgängig gemacht werden und die Kundenpunkte werden entsprechend angepasst.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkDelete}>
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Nach Kunde suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stempel-Typ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                <SelectItem value="visit">Besuch</SelectItem>
                <SelectItem value="referral">Empfehlung</SelectItem>
              </SelectContent>
            </Select>

            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Zeitraum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Zeiten</SelectItem>
                <SelectItem value="today">Heute</SelectItem>
                <SelectItem value="week">Letzte Woche</SelectItem>
                <SelectItem value="month">Letzter Monat</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gesamt Stempel</p>
                <p className="text-2xl font-bold">{stamps.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <User className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Besuchs-Stempel</p>
                <p className="text-2xl font-bold">
                  {stamps.filter(s => s.stamp_type === 'visit').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-500/10 p-3 rounded-lg">
                <Gift className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Empfehlungs-Stempel</p>
                <p className="text-2xl font-bold">
                  {stamps.filter(s => s.stamp_type === 'referral').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stamps Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Stempel Historie ({filteredStamps.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedStamps.length === filteredStamps.length && filteredStamps.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <span className="text-sm text-muted-foreground">Alle auswählen</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredStamps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Keine Stempel gefunden.
            </div>
          ) : (
            <div className="space-y-2">
              {filteredStamps.map((stamp) => (
                <div
                  key={stamp.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50"
                >
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={selectedStamps.includes(stamp.id)}
                      onCheckedChange={() => toggleStampSelection(stamp.id)}
                    />
                    
                    <div className="flex items-center gap-3">
                      <Badge variant={stamp.stamp_type === 'visit' ? 'default' : 'secondary'}>
                        {stamp.stamp_type === 'visit' ? 'Besuch' : 'Empfehlung'}
                      </Badge>
                      
                      <div>
                        <p className="font-medium">{stamp.customer.full_name}</p>
                        <p className="text-sm text-muted-foreground">{stamp.customer.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="text-right">
                      <p>{format(new Date(stamp.created_at), 'dd.MM.yyyy', { locale: de })}</p>
                      <p>{format(new Date(stamp.created_at), 'HH:mm', { locale: de })} Uhr</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="font-medium text-foreground">+{stamp.points_awarded}</p>
                      <p>Punkte</p>
                    </div>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Stempel rückgängig machen</AlertDialogTitle>
                          <AlertDialogDescription>
                            Möchten Sie diesen Stempel für {stamp.customer.full_name} wirklich rückgängig machen? 
                            Die Punkte werden vom Kundenkonto abgezogen.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleUndoStamp(stamp.id)}>
                            Rückgängig machen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </DashboardLayout>
  )
}